<template>
	<v-card v-show="!isSubpageOpen" :class="cardClass()">
		<v-card-text class="flex-grow-0 flex-shrink-0">
			<div v-if="showHeader" class="header">
				<h2 v-if="conversationType == 'async'">
					<span class="action-header-async-call">Request</span> Photos, Videos, or Documents From Your Customer
				</h2>
				<div v-else>
					<h2>Send a <span class="action-header-sync-call">Live Video Call</span> Invitation</h2>
				</div>
			</div>
			<v-form ref="form" v-model="form" :autocomplete="autocomplete" :disabled="isFormDisabled">
				<!--Contact name -->
				<CustomerAutoComplete
					v-if="!phone || selectedCustomer"
					v-model="selectedCustomer"
					:mobile-picker="conversationType === 'async'"
					label="Contact (Optional)"
					:disabled="isFormDisabled"
					density="compact"
					@update:modelValue="customerSelected" />

				<v-text-field
					v-else
					ref="name"
					v-model="name"
					:autocomplete="autocomplete"
					:disabled="disablePhoneNumberEdit"
					hide-details
					variant="outlined"
					density="compact"
					:max-length="56"
					class="field-border-radius">
					<template #label><FieldLabel label="Contact (Optional)" /></template>
				</v-text-field>
				<!--Enter a phone number-->
				<div style="position: relative" @paste.prevent="pasteEvent">
					<v-menu
						v-model="phoneSearchResultsOpen"
						location="bottom"
						:close-on-content-click="false"
						activator="parent"
						:open-on-click="false"
						:min-width="phoneSearchMenuWidth"
						:max-width="phoneSearchMenuWidth"
						max-height="250">
						<v-list v-if="customerItems.length > 0" density="compact">
							<v-list-item
								v-for="item in customerItems"
								:key="item.id"
								@mousedown.prevent
								@click="customerPhoneSelected(item)">
								<v-list-item-title>{{ item.name || item.phone || item.email }}</v-list-item-title>
								<v-list-item-subtitle v-if="item.phone && item.name"
									><span>{{ item.phone }}</span></v-list-item-subtitle
								>
								<v-list-item-subtitle v-if="item.email && (item.name || item.phone)"
									><span>{{ item.email }}</span></v-list-item-subtitle
								>
							</v-list-item>
						</v-list>
						<v-card v-else>
							<v-card-text>No contacts found.</v-card-text>
						</v-card>
					</v-menu>
					<vue-tel-input
						ref="phoneInput"
						:autocomplete="autocomplete"
						tabindex="0"
						autofocus
						:border-radius="0"
						:valid-characters-only="true"
						valid-color="#000000"
						v-bind="vueTelProps"
						v-model="phone"
						:disabled="disablePhoneNumberEdit"
						class="field-border-radius"
						:class="{
							'error--text': showPhoneNumberError
						}"
						:input-options="phoneInputOptions"
						warn-on-typed-country-change
						@validate="validate"
						@on-input="matchPhone"
						@country-changed="matchPhone"
						@blur="phoneBlur"
						@focus="phoneFocus" />

					<!-- LSP-18544: showPhoneFieldErrorState used to colour the field red and say nothing, so a
					     rejected send looked broken rather than fixable. Copy matches the mobile app's
					     ("Please enter a valid phone number.") because the ticket is a web/app parity
					     complaint. vue-tel-input is not a Vuetify field, so the message is rendered directly with
					     Vuetify's own .v-messages typography and text-error colour rather than through v-input. -->
					<div
						v-if="showPhoneNumberError"
						v-test="'video-phone-error'"
						:id="phoneErrorMessageId"
						class="v-messages text-error px-3 pt-1"
						role="alert">
						Please enter a valid phone number.
					</div>
				</div>

				<ProjectAutoComplete
					ref="projectAutoCompleteRef"
					:model-value="newSelectedProject"
					:disabled="isFormDisabled || disableProjectField"
					:entity="null"
					density="compact"
					label="Project (Optional)"
					@update:modelValue="updateProject" />
				<AddressLookupLink v-if="showAddressLookupLink" @openAddressLookup="openAddressLookup" />

				<!-- LSP-20406: tag the session as part of starting the action. -->
				<SessionTagPicker v-model="selectedSessionTags" :disabled="isFormDisabled" />

				<!--Add a message-->
				<!-- LSP-17482: this field is the ONE place where the counter can't use AppTextarea's app-wide
				     code-point unit, because formatNotificationTemplate (apps/contact-api/src/controllers/conversations.ts)
				     EXPANDS ${organization} / ${user} / ${teamMember} and only then rejects on unicodeLength()
				     — plain UTF-16 length, since it sums `charAt(i).length` — at 500 sync / 1000 async. Counting
				     the raw text at a flat 500 made three mismatches reachable: a short placeholder expanding to
				     a long org name read green and then 400'd, a raw-over-limit template whose placeholders
				     SHRINK on expansion was blocked even though the server would have taken it, and an async
				     request was capped 500 short of the server's own limit. -->
				<AppTextarea
					ref="message"
					v-model="notificationTemplate"
					rows="3"
					:disabled="disablePhoneNumberEdit"
					hide-details="auto"
					variant="outlined"
					:character-limit="serverTemplateCharacterLimit"
					over-limit-message="Please enter a shorter message."
					:measure-value="measureServerTemplateLength"
					:placeholder="defaultNotificationTemplate"
					class="field-border-radius"
					@change="notesChanged">
					<template #label><FieldLabel label="Message (Required)" /></template>
				</AppTextarea>

				<div v-if="organization.allowEmailInvitations !== false">
					<v-switch
						ref="includeEmailInvitation"
						v-model="includeEmailInvitation"
						inset
						hide-details
						style="margin-top: -12px !important"
						class="mt-0">
						<template #label><FieldLabel label="Include Email Invitation (Optional)" /></template>
					</v-switch>
					<EmailTextField
						v-show="includeEmailInvitation"
						v-if="includeEmailInvitation || email"
						ref="email"
						v-model.trim="email"
						density="compact"
						:disabled="emailEditDisabled"
						class="field-border-radius email-invite-input"
						variant="outlined"
						:required="includeEmailInvitation"
						:max-length="emailMaxLength"
						:rules="emailRules"
						:validate-email="includeEmailInvitation"
						invalid-message="Please enter a valid email or disable the Include Email Invitation option."
						clearable
						:hint="
							conversationType == 'async'
								? 'Link to create or upload content will be sent to this email.'
								: 'Link to join the meeting will be sent to this email.'
						"
						persistent-hint>
						<template #label><FieldLabel label="Email (Required)" /></template>
					</EmailTextField>
				</div>

				<SmartPackSelector
					v-model="scribePackSelection"
					ref="smartPackSelector"
					@update:model-value="scribePackSelectionTouched = true"
					@update:processing="updateScribePackProcessing"
					@update:user-input-pending="scribeUserInputPending = $event" />
				<v-alert
					v-if="scribeUserInputPending"
					type="info"
					variant="tonal"
					density="compact"
					class="mt-3 cursor-pointer"
					@click="$refs.smartPackSelector?.reopenDialog()">
					<div class="text-body-2">This Spark report requires additional input. Click here to provide the required details.</div>
				</v-alert>
				<ScheduledSparkNotice v-if="scribePackSelection" :delay-seconds="contentNotificationDelay" />
				<InstructionPicker
					v-if="conversationType === 'async'"
					v-model="instructionId"
					class="mt-2"
					:create-return-to="instructionCreateReturnTo"
					create-return-action="request-recording"
					:create-draft-key="instructionReturn.storageKey()"
					:preselect-default="instructionPreselectAllowed"
					@preselect-settled="instructionPreselectAllowed = false"
					@create-start="saveInstructionCreateDraft" />
			</v-form>
		</v-card-text>

		<v-card-actions class="d-flex pa-2 ga-2">
			<!--Close-->
			<v-btn
				variant="outlined"
				rounded="xl"
				elevation="0"
				@click.stop.prevent="close"
				@touchstart.stop.prevent="close()"
				size="x-large"
				style="flex: 1">
				{{ meetingUrl ? "Close" : "Cancel" }}
			</v-btn>

			<!--Start-->
			<PrimaryBtn
				v-if="!meetingUrl || actioningTextOverride"
				:disabled="startButtonDisabled()"
				:loading="starting"
				style="flex: 1"
				@click="start()"
				size="x-large">
				{{ dynamicActioningText }}
			</PrimaryBtn>

			<!--Join-->
			<PrimaryBtn
				v-if="meetingUrl && allowJoin"
				@click="join(null, sendGeneration)"
				size="x-large"
				style="flex: 1"
				icon-prepend="icon-video"
				>Join</PrimaryBtn
			>
		</v-card-actions>
	</v-card>
	<AddressLookup v-if="isAddressLookupOpen" @projectSelected="updateProject" @close="isAddressLookupOpen = false" />
</template>

<script>
	import { useId } from "vue";
	import api from "../api";
	import { resolveProjectPrefill } from "@/helpers/projectPrefill";
	import { isProjectFlow, navigateAfterActionStarted, returnToProjectAfterActionStarted } from "@/helpers/projectFlowReturn";
	import "../helpers/emoji";
	import moment from "moment-timezone";
	import constants from "../api/constants";
	import Dialogs from "../helpers/dialogs";
	import { EventBus } from "../helpers/eventBus";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import SessionTagPicker from "@/components/tags/SessionTagPicker.vue";
	import DeviceUtility from "@/helpers/device";
	import timezones from "@/helpers/timezones";
	import { getDateTokens } from "@/helpers/dateFormat";
	import { closeActionDialog } from "@/helpers/closeActionDialogs";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import { FIELD_LENGTHS, VuetifyValidation } from "../helpers/Validation";
	import {
		getMessageFromError,
		calculateTimeout,
		getTrimmedPhoneFromClipboard,
		serverNotificationTemplateLength
	} from "@/helpers/utilities";
	import AddressLookup from "./location/AddressLookup.vue";
	import AddressLookupLink from "./location/AddressLookupLink.vue";
	import { getSearchLabelForCustomers, getCustomerSearchLabel } from "../formatters/customers";
	import { getVueTelProps } from "@/helpers/phone";
	import { SafeVueTelInput as VueTelInput } from "@/components/SafeVueTelInput";
	import "vue-tel-input/vue-tel-input.css";
	import { useToast } from "vue-toastification";
	import { useOrientationChange } from "@/composables/useOrientationChange";
	import { auth } from "../auth.js";
	import { logError, logInfo } from "../helpers/logError";
	import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
	import { LivePreScreenBody, LivePreScreenHead, writePreScreen } from "../helpers/string";

	import SmartPackSelector from "./scribe/SmartPackSelector.vue";
	import ScheduledSparkNotice from "./scribe/ScheduledSparkNotice.vue";
	import InstructionPicker from "./InstructionPicker.vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { AppTextarea } from "@common-ui/components";
	import {
		useInstructionCreateReturn,
		isValidScribePackSelectionDraft,
		normalizeScribePackSelectionDraft
	} from "@/composables/useInstructionCreateReturn";

	const POPUP_BLOCKED_MSG = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
	const DEFAULT_REQUEST_RECORDING_DRAFT_KEY = "contact:request-recording:create-instruction-draft";

	export default {
		components: {
			ProjectAutoComplete,
			SessionTagPicker,
			CustomerAutoComplete,
			AddressLookup,
			AddressLookupLink,
			VueTelInput,

			SmartPackSelector,
			ScheduledSparkNotice,
			InstructionPicker,
			FieldLabel,
			AppTextarea
		},
		emits: ["close-menu", "started"],
		mixins: [closeActionDialog],
		setup(props) {
			const toast = useToast();
			const { orientationChangeCount } = useOrientationChange();
			const instructionReturn = useInstructionCreateReturn({
				defaultDraftKey: DEFAULT_REQUEST_RECORDING_DRAFT_KEY,
				getDraftKey: () => props.instructionCreateDraftKey,
				label: "Request Content"
			});
			return {
				toast,
				orientationChangeCount,
				instructionReturn,
				// LSP-18544: per-instance so aria-describedby can never point at a duplicate id. ActionDialog
				// mounts this component twice — the sync live-call tab and the async request-content tab.
				phoneErrorMessageId: `video-phone-error-message-${useId()}`
			};
		},
		props: {
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			conversationType: String, //sync or async
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean,
			// LSP-16289: create-and-return detour context, forwarded by ActionDialog only to the
			// async (Request Content) instance. null on the sync (live-call) instance, which has no picker.
			initialInstructionId: {
				type: String,
				default: null
			},
			instructionCreateReturnTo: {
				type: String,
				default: null
			},
			instructionCreateDraftKey: {
				type: String,
				default: null
			}
		},
		data() {
			return {
				emailMaxLength: FIELD_LENGTHS.EMAIL,
				isAddressLookupOpen: false,
				showPhoneFieldErrorState: false,
				autocomplete: Math.random().toString(),
				form: false,
				currentTab: null,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				autoSetNameValue: "",
				emailDisabled: false,
				email: "",
				phone: "",
				name: "",
				includeEmailInvitation: false,
				meetingUrl: "",
				conversationId: "",
				// The project the created conversation actually belongs to, recorded WITH the id so the two
				// travel together. `conversationId` outlives a single start() call, its owner did not, so any
				// later hand-off (start() re-entered, the template Join, a repeated hand-off) re-read the
				// picker and paired an existing conversation with whatever project was selected by then
				// (break-it round 2). null means "no owner recorded" -- an existing conversation adopted from
				// a prop, where the live read is still the best available answer. "" means "created with no
				// project", which must NOT fall through to one.
				conversationProjectId: null,
				actioningTextOverride: "",
				organization: {},
				autoStartRecordings: true,
				notificationTemplate: "",
				me: null,
				customers: [],
				selectedCustomer: null,
				validCustomer: true,
				searchCustomers: null,
				phoneSearchResultsOpen: false,
				customerJustSelected: false,
				newSelectedProject: null,
				projectSelectItems: [],
				searchTerm: "",
				phoneNumberFieldInFocus: false,
				isLiveCallReadyToJoin: false,
				phoneSearchMenuWidth: null,
				disableProjectField: false,
				scribePackSelection: null,
				scribePackSelectionTouched: false,
				scribePackProcessing: false,
				scribeUserInputPending: false,
				instructionId: null,
				// LSP-20840: preselecting the org default is allowed on a fresh open, or after the
				// LSP-16289 restore settles WITHOUT consuming a draft/returned id — a consumed draft
				// (even one whose instructionId is an explicit clear) always wins over the default.
				instructionPreselectAllowed: false,
				// LSP-20406: tags applied to the session at creation.
				selectedSessionTags: [],
				sendGeneration: 0,
				startingGeneration: null
			};
		},
		computed: {
			contentNotificationDelay() {
				return this.me?.config?.contentNotificationDelaySeconds;
			},
			defaultNotificationTemplate() {
				return this.conversationType == "async"
					? this.organization.notificationTemplateAsync
					: this.organization.notificationTemplate;
			},
			isSubpageOpen() {
				return this.isAddressLookupOpen;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				return "Send";
			},
			disablePhoneNumberEdit() {
				return !!this.meetingUrl;
			},
			emailRules() {
				if (!this.includeEmailInvitation) {
					return [];
				}
				const errorTpl = "Please enter a valid email or disable the Include Email Invitation option.";
				return [VuetifyValidation.RequiredValidationField(errorTpl).isValid];
			},
			emailEditDisabled() {
				return this.emailDisabled || !this.includeEmailInvitation || this.disablePhoneNumberEdit;
			},
			customerItems() {
				return getSearchLabelForCustomers(this.customers);
			},
			isFormDisabled() {
				return this.isLiveCallReadyToJoin;
			},
			showAddressLookupLink() {
				return !this.isFormDisabled && !this.disableProjectField;
			},
			serverTemplateCharacterLimit() {
				// The server's own two limits, keyed off the same conversation type it keys off
				// (formatNotificationTemplate: 1000 for an async request, 500 otherwise). Compared
				// case-insensitively because the server derives isAsyncRequest with constants.areEqual,
				// which upper-cases both sides — a strict === here would cap a "Async" request 500 short of
				// what the server accepts. break-it round 1
				const requestedType = String(this.conversationType ?? "").toUpperCase();
				return requestedType === String(constants.CONVERSATION_TYPE.ASYNC).toUpperCase() ? 1000 : 500;
			},
			phoneInputOptions() {
				// LSP-18544: points the phone input at the error message so a screen reader meets it when
				// tabbing control-to-control, not only when role="alert" fires on insertion. Overriding
				// input-options after v-bind="vueTelProps" is the same pattern WebFormV2.vue uses;
				// aria-describedby is one of vue-tel-input's documented InputOptions keys.
				return {
					...this.vueTelProps.inputOptions,
					"aria-describedby": this.showPhoneNumberError ? this.phoneErrorMessageId : undefined
				};
			},
			showPhoneNumberError() {
				// LSP-18544: true exactly when the user has attempted (or blurred) AND the number would
				// still fail schedule()/start()'s guard. Mirroring the guard's own condition rather than
				// just `!validPhone` matters: vue-tel-input does not always re-emit @validate when the
				// field is cleared, so an emptied field can sit with a stale validPhone === true — which
				// still blocks submit, and would otherwise block it in silence all over again. Drives the
				// red field state as well as the message, so the two can never disagree.
				return this.showPhoneFieldErrorState && !(this.phone && this.validPhone);
			},
			measureServerTemplateLength() {
				// Bound to AppTextarea's measure-value, so the counter and the over-limit rule both report the
				// UTF-16 length of the placeholder-EXPANDED template — the string formatNotificationTemplate
				// actually validates. The placeholder values must be the ones the server picks, which is why
				// getTeamMemberName() mirrors its fallback order exactly (LSP-17482).
				const organizationName = this.organization?.name ?? "";
				const userName = this.getTeamMemberName();
				return (value) => serverNotificationTemplateLength(value, { organizationName, userName });
			}
		},
		watch: {
			searchCustomers(searchTerm) {
				this.searchTerm = searchTerm;
				clearTimeout(this._searchTimerId);
				this._searchTimerId = setTimeout(async () => {
					const customers = await Dialogs.getCustomers(searchTerm);
					this.customers = customers;
				}, 500);
			},
			phone: {
				async handler(newValue) {
					if (!newValue) {
						this.customerJustSelected = false;
						this.deactivateCustomerPhoneSearchMenu();
						const customers = await Dialogs.getCustomers(newValue);
						this.customers = customers;
						return;
					}

					if (this.customerJustSelected) {
						// The user just selected one of search results so we don't want to do a search/open the menu
						return;
					}

					clearTimeout(this._searchTimerId);
					this._searchTimerId = setTimeout(async () => {
						const customers = await Dialogs.getCustomers(newValue);
						this.customers = customers;

						if (customers.length > 0) {
							this.activateCustomerPhoneSearchMenu();
						} else {
							this.deactivateCustomerPhoneSearchMenu();
						}
					}, 500);
				}
			},
			orientationChangeCount() {
				this.$refs.projectAutoCompleteRef?.blur?.();
				this.deactivateCustomerPhoneSearchMenu();
				this.syncPhoneSearchMenuWidth();
				this.$refs.phoneInput?.blur?.();
			}
		},
		activated() {
			try {
				this.$refs.phoneInput.focus();
			} catch {
				// Ignore focus failures when the vue-tel-input ref has not yet been created.
			}
		},
		beforeUnmount() {
			this.sendGeneration += 1;
			clearTimeout(this._sentTimerId);
			this._sentTimerId = null;
		},
		async mounted() {
			const returnedInstruction =
				(this.$route.path || "").replace(/\/+$/, "").toLowerCase() === "/request-recording"
					? this.instructionReturn.consumeReturnQuery(this.$route, this.$router, "request-recording")
					: null;
			await this.reset();
			this.tryOpenAddressLookup();
			this.me = await api.getMe();
			this.organization = this.me.organization;
			this.autoStartRecordings = this.organization.autoStartRecordings;
			this.notificationTemplate = this.updateTemplate(this.defaultNotificationTemplate);

			if (this.projectId || this.project) {
				this.disableProjectField = true;
			}

			this.customers = await Dialogs.getCustomers();

			// LSP-16205: when opened from a project, default the contact to the project's
			// first assigned contact so the user isn't forced to re-search. An explicit
			// customerId prop still takes precedence, and the prefilled value stays editable.
			const { project: resolvedProject, customerId: prefillCustomerId } = await resolveProjectPrefill({
				customerId: this.customerId,
				projectId: this.projectId,
				project: this.newSelectedProject ?? this.project
			});
			if (resolvedProject) {
				this.newSelectedProject = resolvedProject;
			}

			if (prefillCustomerId) {
				try {
					const result = await api.getCustomers({
						where: {
							id: prefillCustomerId
						}
					});

					if (result.count > 0) {
						const customer = getCustomerSearchLabel(result.rows[0]);
						this.setCustomer(customer);
					}
				} catch (error) {
					// A failed prefill is non-critical — degrade to "no contact prefilled" rather than
					// leaving mounted() to reject, matching the other Action* dialogs.
					console.warn(`[LSP-16205] contact prefill lookup failed for customer ${prefillCustomerId}`, error);
				}
			}

			// LSP-16289: restore the in-progress draft only when a return-handoff signal is present.
			// The null check (not truthiness) is intentional: an empty-string initialInstructionId is
			// still an explicit return signal; only null (default) means "fresh open, no handoff."
			if (returnedInstruction || this.instructionCreateDraftKey !== null || this.initialInstructionId !== null) {
				await this.restoreInstructionCreateDraft();
			} else {
				// LSP-20840: fresh open with no handoff context at all — the default may preselect.
				this.instructionPreselectAllowed = true;
			}
		},
		methods: {
			updateScribePackProcessing(processing) {
				this.scribePackProcessing = processing;
				if (processing) this.scribePackSelectionTouched = true;
			},
			warnStaleSend(awaitBoundary, sendGeneration, conversationId = this.conversationId || undefined) {
				console.warn(`ActionVideo: stale send after ${awaitBoundary}; skipping continuation`, {
					awaitBoundary,
					sendGeneration,
					currentSendGeneration: this.sendGeneration,
					conversationId
				});
			},
			async getProject(projectId) {
				if (!projectId) {
					return null;
				}
				try {
					const result = await api.getProject(projectId);
					return result;
				} catch (error) {
					console.error(error);
					return null;
				}
			},
			syncPhoneSearchMenuWidth() {
				this.$nextTick(() => {
					const phoneInputEl = this.$refs.phoneInput?.$el || null;
					const phoneInputRect = phoneInputEl?.getBoundingClientRect?.() || null;
					this.phoneSearchMenuWidth = phoneInputRect?.width ? Math.round(phoneInputRect.width) : null;
				});
			},
			updateProject(project) {
				this.isAddressLookupOpen = false;
				this.newSelectedProject = project;
			},
			cardClass() {
				return `pa-1 conversation-card  ${DeviceUtility.isMobile ? "conversation-bottom" : ""} ${this.conversationType}`;
			},
			startButtonDisabled() {
				return this.starting || !!this.actioningTextOverride || this.scribePackProcessing;
			},
			async reset() {
				this.sendGeneration += 1;
				this.$refs.smartPackSelector?.reset?.();
				this.scribePackSelectionTouched = false;
				// Cancel any pending post-send timer so dismissing the dialog (which calls
				// reset() via the closeActionDialog mixin) cannot fire close() and
				// navigateToCreatedConversation() on a component the user already left.
				clearTimeout(this._sentTimerId);
				this._sentTimerId = null;
				// LSP-17201: that timer's callback was the ONLY code that cleared
				// actioningTextOverride back to "". Cancelling the timer above without mirroring
				// that cleanup left the button stuck reading "Sent!" AND disabled - both
				// dynamicActioningText() and startButtonDisabled() key off this one string - so
				// resetting the dialog during the Sent! handoff made Send permanently unusable.
				this.actioningTextOverride = "";
				this.customerJustSelected = false;
				this.isAddressLookupOpen = false;
				this.meetingUrl = "";
				this.phone = "";
				// LSP-18544 break-it round 1: reset() cleared the phone but not the error state, so a
				// blocked send followed by Reset (or a dialog close, which calls reset() through the
				// closeActionDialog mixin) left "Please enter a valid phone number." accusing an empty
				// field. validPhone goes with it — vue-tel-input does not re-emit @validate on a
				// programmatic clear, so leaving it true would make an empty field look valid.
				this.showPhoneFieldErrorState = false;
				this.validPhone = false;
				this.email = "";
				this.includeEmailInvitation = false;
				this.name = "";
				this.autoSetName = "";
				this.autoSetNameValue = "";
				this.selectedCustomer = null;
				this.scribePackSelection = null;
				this.selectedSessionTags = [];
				this.instructionId = null;
				this.scribePackProcessing = false;
				this.scribeUserInputPending = false;
				this.newSelectedProject = this.project ?? (this.projectId ? await this.getProject(this.projectId) : null);
				this.notificationTemplate = this.updateTemplate(this.defaultNotificationTemplate);
				this.isLiveCallReadyToJoin = false;

				setTimeout(() => {
					try {
						this.$refs.phoneInput.focus();
					} catch {
						// Ignore focus failures when the input is not mounted yet.
					}
				}, 500);
			},
			pasteEvent(event) {
				this.phone = getTrimmedPhoneFromClipboard(event);
				// If a user pastes into the vue-tel-input, force it to reformat itself.
				this.$nextTick(function () {
					try {
						this.$refs.phoneInput.onInput();
					} catch {
						// Ignore formatting refresh failures when the input is not mounted yet.
					}
				});
			},
			// Fires when the correctness of the vue-tel-input's phone number changes (from true to false or vice-versa) and when the component is mounted.
			validate(args) {
				this.validPhone = args.valid;
			},
			// Fires when the vue-tel-input's input changes.
			async matchPhone(number, phoneObject) {
				// Check for field validation if the field was modified when not focused.
				// LSP-18544 break-it round 2: this LATCHES the flag on, it never clears it. Clearing it here
				// erased the explanation while the submit guard was still blocking — ActionVideo's
				// matchPhone runs on @country-changed with the field blurred, so picking a different country
				// after a blocked send wiped both the red border and the message while start() still bailed
				// on `!this.phone`. Hiding is showPhoneNumberError's job: it goes false the moment the number
				// is actually submittable, so nothing needs to clear the flag except reset().
				if (!this.phoneNumberFieldInFocus && Boolean(this.phone) && !this.validPhone) {
					this.showPhoneFieldErrorState = true;
				}

				if (!this.validPhone || !phoneObject || !phoneObject.valid) {
					// bail on invalid numbers
					if (phoneObject && !phoneObject.formatted && this.selectedCustomer?.id) {
						this.clearCustomer();
						return;
					} else if (phoneObject && phoneObject.formatted?.length > 0 && !phoneObject.valid) {
						this.setCustomer(null, true);
						return;
					} else {
						return;
					}
				}

				// Make sure that even if the user changes the formatting of their phone number, we always internally
				// update it to formatted version. This way, when the phone number gets saved to the database, it is
				// always properly formatted.
				this.$nextTick(() => {
					this.phone = phoneObject.formatted;
				});
				this.deactivateCustomerPhoneSearchMenu();

				if (this.phone == "") {
					this.clearCustomer();
					return;
				}

				// Use the formatted phone number
				number = phoneObject?.formatted || number;

				if (!number && this.autoSetName) {
					this.name = "";
				}

				if (this.customerJustSelected) {
					this.customerJustSelected = false;
					return;
				}

				const result = await api.getCustomers({
					where: {
						"$PhoneNumbers.phoneNumber$": {
							$like: `%${number}%`
						}
					}
				});

				if (result.count === 1) {
					const customer = getCustomerSearchLabel(result.rows[0]);
					if (customer) {
						this.customerPhoneSelected(customer);
						clearTimeout(this._searchTimerId);
						return;
					}
				}

				if (result.count > 0) {
					this.customers = result.rows;
					this.activateCustomerPhoneSearchMenu();
					clearTimeout(this._searchTimerId);
				} else {
					this.setCustomer(null, true, this.searchTerm);
				}
			},
			customerSelected(customer) {
				if (customer) {
					this.customerJustSelected = true;
				}

				this.setCustomer(customer);
			},
			customerPhoneSelected(customer) {
				this.setCustomer(customer);
				this.phone = customer.phone || "";
				this.deactivateCustomerPhoneSearchMenu();
				this.customerJustSelected = true;
			},
			phoneBlur() {
				this.phoneNumberFieldInFocus = false;
				this.deactivateCustomerPhoneSearchMenu();
				// LSP-18544 break-it round 2: this LATCHES the flag on, it never clears it. Clearing it here
				// erased the explanation while the submit guard was still blocking — ActionVideo's
				// matchPhone runs on @country-changed with the field blurred, so picking a different country
				// after a blocked send wiped both the red border and the message while start() still bailed
				// on `!this.phone`. Hiding is showPhoneNumberError's job: it goes false the moment the number
				// is actually submittable, so nothing needs to clear the flag except reset().
				if (Boolean(this.phone) && !this.validPhone) {
					this.showPhoneFieldErrorState = true;
				}
			},
			phoneFocus() {
				this.phoneNumberFieldInFocus = true;
				// LSP-18544 break-it round 1: this used to clear the error state on focus. When the error
				// state was colour-only that was harmless; now it would erase the only statement of WHAT
				// to fix at the exact moment the user clicks in to fix it. showPhoneNumberError already
				// hides itself the instant the number becomes valid, so nothing needs clearing here.
				this.syncPhoneSearchMenuWidth();
			},
			activateCustomerPhoneSearchMenu() {
				if (this.phone && this.phone.length > 0 && this.phoneNumberFieldInFocus) {
					this.phoneSearchResultsOpen = true;
					this.syncPhoneSearchMenuWidth();
				}
			},
			deactivateCustomerPhoneSearchMenu() {
				this.phoneSearchResultsOpen = false;
			},
			async clearCustomer() {
				this.customerJustSelected = false;
				this.setCustomer(null);
				this.phone = "";
				this.customers = await Dialogs.getCustomers();
			},
			setCustomer(customer, filtering = false, searchTerm = "") {
				this.selectedCustomer = customer;
				if (customer) {
					this.validCustomer = true;
					this.name =
						customer.firstname && customer.lastname
							? `${customer.firstname} ${customer.lastname}`
							: customer.firstname || customer.lastname;
					this.autoSetName = true;
					this.autoSetNameValue = this.name;
					const emailInvitationsAllowed = this.organization.allowEmailInvitations !== false;

					if (customer.EmailAddresses?.length > 0 && emailInvitationsAllowed) {
						this.includeEmailInvitation = true;
						this.email = customer.EmailAddresses[0].emailAddress;
						this.emailDisabled = true;
					} else {
						this.email = "";
						this.emailDisabled = false;
						this.includeEmailInvitation = false;
					}

					if (customer.PhoneNumbers?.length > 0) {
						if (!this.phone || this.customerJustSelected) {
							this.phone = customer.PhoneNumbers[0].phoneNumber;
						}
					} else {
						this.phone = "";
					}
				} else {
					this.validCustomer = false;
					this.name = searchTerm;
					if (this.emailDisabled) {
						this.email = "";
						this.emailDisabled = false;
						this.includeEmailInvitation = false;
					}
					if (!filtering) {
						this.phone = "";
					}
				}
			},
			getTeamMemberName() {
				// Mirrors formatNotificationTemplate's placeholder-user resolution exactly (LSP-17482): the
				// counter can only agree with the server if it substitutes the same string the server will.
				// The old `${name} ${lastname}` unconditionally stringified a null firstname, so a
				// lastname-only user got a literal "null Smith" both in the counter AND in the sent message.
				//
				// break-it round 1: `me.user` is optional-chained too (this now runs inside a render-time
				// computed, so a /me payload without a user object would blank the dialog rather than just
				// mis-name a message), and the old `?? "-"` fourth tier is gone — the server's last tier is
				// `email || ""`, so inventing a dash made every ${user} count one unit more than the server
				// measures, in exactly the window before getMe() resolves.
				let name = "";

				if (this.me?.user?.firstname) {
					name = this.me.user.firstname;
				}

				if (this.me?.user?.lastname) {
					name = name ? `${name} ${this.me.user.lastname}` : this.me.user.lastname;
				}

				if (!name) {
					name = this.me?.user?.email;
				}

				return name ?? "";
			},
			updateTemplate(template) {
				if (!template) {
					return "";
				}

				const teamMember = this.getTeamMemberName();

				return template
					.replaceAll("${teamMember}", teamMember)
					.replaceAll("${user}", teamMember)
					.replaceAll("${organization}", this.organization.name);
			},
			notesChanged(notes) {
				// If the user removes the text is in the 'notes' text area, set their
				// notification template to use the default notification template.
				// Fix: Check this.notificationTemplate or event value as notes is an Event object in Vue 3
				const value = notes?.target?.value ?? notes;
				if (!String(value ?? "").trim() || !String(this.notificationTemplate ?? "").trim()) {
					this.notificationTemplate = this.updateTemplate(this.defaultNotificationTemplate);
				}
			},
			isCurrentSend(sendGeneration) {
				return sendGeneration === this.sendGeneration;
			},
			async join(preOpenedWin = null, sendGeneration = null) {
				// Only matters for a join with no recorded owner -- the template-driven one, which never
				// created a conversation. join() awaits a token before it navigates, so capture at entry
				// rather than at the navigate call; conversationProjectId takes precedence over it there.
				const returnProjectId = this.newSelectedProject?.id ?? this.projectId;
				const isStale = () => sendGeneration !== null && !this.isCurrentSend(sendGeneration);
				const win = preOpenedWin ?? window.open("", "_blank");
				const logContext = {
					hasPreOpenedWin: !!preOpenedWin,
					isLiveCallReadyToJoin: this.isLiveCallReadyToJoin,
					conversationType: this.conversationType
				};

				if (!preOpenedWin && win) {
					// Direct-from-Join-button path: still inside a user gesture, so render
					// the loading shell before any awaits so the user never sees about:blank.
					this.renderPopupLoadingShell(win);
				}

				let url = this.meetingUrl;
				if (!url) {
					// Without a meetingUrl there is nothing to navigate to. Don't leave the
					// popup orphaned at about:blank — close it and surface the failure.
					logError("ActionVideo.join: meetingUrl was empty when join() ran", new Error("empty meetingUrl"), logContext);
					this.safelyClosePopup(win);
					const message = "Something went wrong starting the call. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
					return;
				}

				try {
					const token = await auth.getAccessToken();
					if (isStale()) {
						this.warnStaleSend("auth.getAccessToken resolved", sendGeneration);
						this.safelyClosePopup(win);
						return;
					}
					if (token) {
						const urlObj = new URL(url);
						urlObj.hash = "auth_token=" + encodeURIComponent(token);
						url = urlObj.toString();
					} else {
						// getAccessToken() returns null on transient/network failures (it swallows
						// non-terminal Auth0 errors). Without a token the live app can't authenticate,
						// so log a breadcrumb instead of silently navigating to a guaranteed-broken page.
						// Behavior change for this case is deferred — the live app may handle missing
						// tokens via its own auth redirect, so we preserve existing semantics.
						logError(
							"ActionVideo.join: auth.getAccessToken returned null — live app will load without auth_token",
							new Error("null access token"),
							logContext
						);
					}
				} catch (e) {
					if (isStale()) {
						this.warnStaleSend("auth.getAccessToken rejected", sendGeneration);
						this.safelyClosePopup(win);
						return;
					}
					// LSP-17079: getAccessToken() now THROWS TransientAuthTokenError on a transient/offline
					// flap (instead of returning null). Treat that exactly like the null branch above — the
					// live app loads without auth_token and handles its own auth — so a network blip while
					// starting a call does NOT close the popup or force a re-login. This is the flap scenario
					// this ticket targets. Genuine failures (e.g. a malformed meetingUrl in new URL(url)) still
					// close the popup and surface the auth error.
					if (isTransientAuthTokenError(e)) {
						logInfo("ActionVideo.join: transient auth token failure — live app will load without auth_token", logContext);
					} else {
						// Either auth.getAccessToken() threw a non-transient error or new URL(url) threw on a
						// malformed meetingUrl. On-call: check the error stack to disambiguate before assuming Auth0.
						logError("ActionVideo.join: auth lookup or URL build threw", e, logContext);
						this.safelyClosePopup(win);
						const message = "Unable to get authorization. Please try logging in again.";
						this.toast.error(message, { timeout: calculateTimeout(message) });
						return;
					}
				}
				if (isStale()) {
					this.warnStaleSend("auth.getAccessToken processing completed", sendGeneration);
					this.safelyClosePopup(win);
					return;
				}

				if (!win || win.closed) {
					if (DeviceUtility.isIOS || DeviceUtility.isIpad) {
						window.location.href = url;
					} else if (!win) {
						this.toast.error(POPUP_BLOCKED_MSG, { timeout: calculateTimeout(POPUP_BLOCKED_MSG) });
					} else {
						// Popup was opened but the user (or browser) closed it before we could navigate it.
						// Redirect the current tab as the user's escape hatch.
						logError("ActionVideo.join: pre-opened popup was closed before navigation", new Error("popup closed"), logContext);
						window.location.href = url;
					}
					return;
				}

				try {
					win.location.href = url;
				} catch (e) {
					// Safari/Chrome can detach the popup reference during a long async gap. Setting
					// location.href on a detached window throws; without this catch the popup stays
					// orphaned at about:blank with no user feedback (the original LSP-15903 regression).
					logError("ActionVideo.join: failed to navigate pre-opened popup", e, logContext);
					this.safelyClosePopup(win);
					// No toast here: the current-tab navigation below is itself the user feedback,
					// and a toast would be discarded before render when the page unloads.
					window.location.href = url;
					return;
				}
				// Kept outside the popup-error try/catch so a throw here (e.g., a parent's
				// synchronous close-menu handler) cannot trick that catch into tearing down a
				// popup that already navigated successfully. Also locally try/caught so a
				// routing failure cannot propagate to start()'s outer catch, which would
				// close the now-successfully-navigated popup and toast an error for what
				// was actually a success.
				try {
					this.navigateToCreatedConversation({ projectIdForReturn: returnProjectId });
				} catch (e) {
					logError("ActionVideo.join: navigateToCreatedConversation threw after popup nav", e, logContext);
				}
			},
			renderPopupLoadingShell(win) {
				// Safari can throw when touching the popup's document under certain timings —
				// the loading shell is best-effort UX, not load-bearing.
				try {
					if (win?.document?.body) {
						writePreScreen(win, LivePreScreenHead, LivePreScreenBody(window.location.origin));
					}
				} catch (e) {
					logError("ActionVideo: failed to render popup loading shell", e);
				}
			},
			safelyClosePopup(win) {
				// close() can throw on a window whose opener relationship was severed.
				try {
					win?.close();
				} catch (e) {
					logError("ActionVideo: failed to close popup", e);
				}
			},
			// Three sources, in descending order of how well they know who owns this conversation:
			// conversationProjectId (recorded at create time, so it survives a re-entered start(), a
			// repeated hand-off and the template Join -- break-it round 2); then projectIdForReturn,
			// which join() captures at entry for the case where no conversation was created here; then
			// a live read, which is all that is left when neither applies. Reading the picker live
			// FIRST was the original defect (PR #3891 review): it returns the user to whatever they
			// switched to during the await, which does not own the new conversation.
			navigateToCreatedConversation({ projectIdForReturn = null } = {}) {
				if (!this.conversationId) {
					console.warn("ActionVideo.navigateToCreatedConversation: missing conversationId; skipping nav", {
						meetingUrl: this.meetingUrl,
						conversationType: this.conversationType
					});
					return;
				}
				// LSP-15388: on mobile the FAB routes here as a standalone /request-recording or /live-call
				// page, which unmounts the project. close-menu alone only works for the desktop dialog that
				// sits over a still-mounted project page.
				if (!isProjectFlow(this.$route, "ActionVideo")) {
					// ls-heroku-ci (HIGH): through the shared helper, like the project branch below. A plain
					// try/catch upstream catches a SYNCHRONOUS throw, but vue-router RESOLVES with a
					// NavigationFailure for an aborted/cancelled/duplicated navigation and can reject from a
					// guard — neither reaches a catch, which is exactly what this helper exists to observe.
					navigateAfterActionStarted({
						router: this.$router,
						target: { name: "conversation", params: { id: this.conversationId }, query: { from: "homepage" } },
						componentName: "ActionVideo",
						context: { conversationId: this.conversationId },
						destination: "to the conversation"
					});
				} else {
					// ls-heroku-ci (HIGH): a parent's close-menu listener is someone else's code and can throw
					// synchronously. join()'s call to this method is wrapped for exactly that reason, but the
					// start() request path is not — it sits inside start()'s outer try, whose catch closes the
					// popup and shows a generic error toast for a send that set actioningTextOverride = "Sent!"
					// two lines earlier. Guarded locally, the same way ActionPhoto, ActionStartRecording and
					// ActionUploadPhoto guard their own close-menu hand-offs.
					try {
						this.$emit("close-menu", this.conversationId);
					} catch (error) {
						console.warn("ActionVideo.navigateToCreatedConversation: a close-menu consumer failed, continuing the hand-off", {
							tags: { method: "navigateToCreatedConversation", file: "ActionVideo" },
							extra: { conversationId: this.conversationId, error }
						});
					}
					returnToProjectAfterActionStarted({
						route: this.$route,
						router: this.$router,
						projectId: this.conversationProjectId ?? projectIdForReturn ?? this.newSelectedProject?.id ?? this.projectId,
						componentName: "ActionVideo"
					});
				}
			},
			async start() {
				if (this.startButtonDisabled()) {
					return;
				}
				if (!this.phone || !this.validPhone) {
					// LSP-18544: this gate used to exit silently, so "the Send button does nothing" was
					// indistinguishable from "the phone number is malformed". Log which condition fired.
					console.warn("[LSP-18544] ActionVideo.start blocked: phone number not valid", {
						hasPhone: Boolean(this.phone),
						validPhone: this.validPhone,
						conversationType: this.conversationType,
						projectId: this.projectId ?? null,
						conversationId: this.conversationId ?? null
					});
					this.showPhoneFieldErrorState = true;
					return;
				}
				const sendGeneration = ++this.sendGeneration;
				if (!this.form) {
					await this.$refs.form.validate();
					return;
				}
				if (this.projectId && !this.newSelectedProject) {
					// LSP-18544 break-it round 2: the sibling silent return one line below the phone guard, with
					// the same "the button does nothing" signature. getProject() swallows its fetch error, so a
					// 403/500 on GET /projects/:id leaves this permanently unresolved with no UI feedback at all.
					// CLAUDE.md requires logging at EVERY fail-condition in a gate a silent-failure fix touches.
					console.warn("[LSP-18544] ActionVideo.start blocked: project not resolved", {
						projectId: this.projectId,
						hasSelectedProject: Boolean(this.newSelectedProject)
					});
					return;
				}
				if (this.scribeUserInputPending) {
					return;
				}

				// ls-heroku-ci: ONE identity for the whole send, captured before any await, matching the fix
				// already applied to ActionPhoto, ActionStartRecording and ActionUploadPhoto. The contact
				// back-fill below runs after api.getMe() and read the project and contact LIVE, so switching
				// either while that await was in flight wrote the WRONG contact into the WRONG project's
				// Customers list -- a persistent api.updateProject write the customer never asked for.
				//
				// Used for the create request and the return target too, deliberately: snapshotting only the
				// back-fill would leave those reading live, and one send could then file the contact against
				// one project while creating the conversation in another. References, not copies -- the hazard
				// is the picker emitting a different object, and the in-place Customers mutation below is
				// unchanged.
				const actionProject = this.newSelectedProject;
				const actionContact = this.selectedCustomer;
				// The identity fields belong to the same snapshot: the form stays editable until join, so
				// reading them after the awaits below could back-fill the contact captured above onto the
				// project while inviting whoever the form names by then -- two different people.
				const actionName = this.name;
				const actionPhone = this.phone;
				const actionEmail = this.email;
				const actionScribePackSelection = this.scribePackSelection;
				// LSP-20406: captured with the other send inputs so a selection edit during the awaited
				// create cannot change which tags the request carries.
				const actionSessionTags = [...this.selectedSessionTags];
				this.starting = true;
				this.startingGeneration = sendGeneration;

				// Safari Fix (LSP-15903) - Pre-open the tab synchronously inside the user
				// gesture, then render a loading shell so it never visibly sits at about:blank
				// during the async createConversation chain. join() redirects it once the
				// meeting URL is ready. iOS/iPad fall back to navigating the current tab
				// inside join() when window.open returns null.
				let preOpenedWin = null;
				if (this.allowJoin) {
					preOpenedWin = window.open("", "_blank");
					if (!preOpenedWin) {
						if (!(DeviceUtility.isIOS || DeviceUtility.isIpad)) {
							this.starting = false;
							this.startingGeneration = null;
							logError("ActionVideo.start: window.open returned null on non-iOS device", new Error("popup blocked"));
							this.toast.error(POPUP_BLOCKED_MSG, { timeout: calculateTimeout(POPUP_BLOCKED_MSG) });
							return;
						}
					} else {
						this.renderPopupLoadingShell(preOpenedWin);
					}
				}

				try {
					let baseUrl = window.env.VITE_LIVE_DOMAIN;
					if (this.conversationType === "async") {
						baseUrl = window.env.VITE_ASYNC_DOMAIN;
					}

					const me = await api.getMe();
					if (!this.isCurrentSend(sendGeneration)) {
						this.warnStaleSend("api.getMe resolved", sendGeneration);
						this.safelyClosePopup(preOpenedWin);
						return;
					}
					const selectedUserId = me.user.id;
					const nameParts = (actionName || "").trim().split(" ");
					const first = nameParts.length > 0 ? nameParts[0] : "";
					const last = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

					if (actionProject?.id && actionContact?.id) {
						const selectedContact = actionContact;
						let contactExistance = false;
						if (!actionProject.Customers) {
							actionProject.Customers = [];
						}
						for (const contact of actionProject.Customers) {
							if (contact.id === selectedContact.id) {
								contactExistance = true;
								break;
							}
						}
						if (!contactExistance) {
							await api.updateProject(actionProject.id, {
								Customers: [...actionProject.Customers, selectedContact]
							});
							if (!this.isCurrentSend(sendGeneration)) {
								this.warnStaleSend("api.updateProject resolved", sendGeneration);
								this.safelyClosePopup(preOpenedWin);
								return;
							}
						}
					}
					let conversationName;
					if (this.conversationType == constants.CONVERSATION_TYPE.ASYNC) {
						let titlePrefix = "Content from";

						let name = first || last || this.phone?.trim() || this.email?.trim();
						conversationName = titlePrefix + " " + name;
						// Use the org from the fresh getMe() fetched above (line ~886), not the mount-time
						// this.organization, so a date-format change made after this dialog opened is honored
						// in the customer-visible name (LSP-15050).
						const formattedDate = timezones.formatDate(
							new Date().toISOString(),
							getDateTokens(me?.organization?.dateFormat).dateShortMonth
						);
						conversationName = conversationName + " on " + formattedDate;
					}
					this.notesChanged(this.notificationTemplate);
					const includeEmailInvitation = this.organization.allowEmailInvitations !== false && this.includeEmailInvitation;
					// Captured BEFORE the awaited createConversation, mirroring ActionUploadPhoto's
					// projectIdForReturn: a project change during this await must not redirect the user to
					// a project that does not own the new conversation (PR #3891 review).
					// Exactly what the request below carries -- `?? ""` rather than `?? this.projectId`, so
					// a create with no project cannot be redirected to one anyway, and the empty string
					// survives the `??` defaults in join()/navigateToCreatedConversation instead of falling
					// back to a live read (break-it round 1). Those defaults still apply to the
					// template-driven join(), which has no request to be consistent with.
					const projectIdForReturn = actionProject?.id ?? "";
					// save data
					const result = await api.createConversation({
						referrer: baseUrl,
						autoStartRecordings: this.organization.autoStartRecordings,
						forcedUserId: selectedUserId,
						notificationSMS: false,
						notificationEmail: includeEmailInvitation,
						name: conversationName,
						notificationTemplate: this.notificationTemplate?.trim(),
						screenShareSupported: this.conversationType != constants.CONVERSATION_TYPE.ASYNC,
						visitors: [
							{
								phone: actionPhone?.trim(),
								email: actionEmail?.trim(),
								firstname: first,
								lastname: last,
								notificationSMS: true,
								notificationEmail: includeEmailInvitation
							}
						],
						projectId: actionProject?.id ?? undefined,
						type: this.conversationType,
						initiationType: "admin",
						timeZone: moment.tz.guess(),
						scribePackId: actionScribePackSelection?.pack?.id ?? undefined,
						scribePackName: actionScribePackSelection?.pack?.name ?? undefined,
						scribePackTaskIds: actionScribePackSelection?.taskIds ?? undefined,
						scribePackUserInputData: actionScribePackSelection?.userInputData ?? undefined,
						instructionId: this.conversationType === "async" ? (this.instructionId ?? undefined) : undefined,
						// LSP-20406: attach the picked tags atomically at creation (server upsertRelatedLabels).
						Labels: actionSessionTags.length ? actionSessionTags : undefined
					});
					if (!this.isCurrentSend(sendGeneration)) {
						this.warnStaleSend("api.createConversation resolved", sendGeneration, result.conversation?.id);
						this.safelyClosePopup(preOpenedWin);
						return;
					}

					this.meetingUrl = result.url;
					this.conversationId = result.conversation?.id ?? "";
					// Paired from the pre-await capture, never a fresh read of the picker.
					this.conversationProjectId = projectIdForReturn;
					this.isLiveCallReadyToJoin = true;

					if (this.allowJoin) {
						// Awaited preventatively: join() currently handles all its failure modes
						// internally (close + toast + return), but a future throw added after an
						// await would otherwise become an unhandled promise rejection. Outer
						// catch would double-toast if join() ever both toasts and throws — keep
						// the invariant "join() either toasts and returns, or throws cleanly".
						await this.join(preOpenedWin, sendGeneration);
						if (!this.isCurrentSend(sendGeneration)) {
							this.warnStaleSend("join resolved", sendGeneration);
							return;
						}
						// Ownership of the popup transferred to join() (it has either navigated
						// the popup to the meeting URL or closed it on the failure path). Drop
						// our reference so a subsequent throw in this try (e.g., a synchronous
						// @started listener) cannot route the outer catch into tearing down a
						// popup the user is now actively using.
						preOpenedWin = null;
					} else {
						this.actioningTextOverride = "Sent!";
						clearTimeout(this._sentTimerId);
						this._sentTimerId = window.setTimeout(() => {
							if (!this.isCurrentSend(sendGeneration)) {
								this.warnStaleSend("Sent timer elapsed", sendGeneration);
								return;
							}
							this._sentTimerId = null;
							this.actioningTextOverride = "";
							this.close();
							this.navigateToCreatedConversation();
						}, 1500);
						EventBus.$emit("UpdateDashboard");
					}

					if (!this.isCurrentSend(sendGeneration)) {
						this.warnStaleSend("before started emit", sendGeneration);
					} else {
						this.$emit("started");
					}
				} catch (e) {
					if (!this.isCurrentSend(sendGeneration)) {
						this.warnStaleSend("start request rejected", sendGeneration);
						this.safelyClosePopup(preOpenedWin);
						return;
					}
					this.safelyClosePopup(preOpenedWin);
					const message = getMessageFromError(e);
					this.toast.error(message, {
						timeout: calculateTimeout(message)
					});

					console.error(e, {
						tags: {
							method: "start",
							file: "ActionVideo"
						},
						extra: {
							type: this.conversationType,
							phone: this.phone
						}
					});
				} finally {
					if (this.startingGeneration === sendGeneration) {
						this.starting = false;
						this.startingGeneration = null;
					}
				}
			},
			tryOpenAddressLookup() {
				const params = new URLSearchParams(window.location.search);
				const goTo = params.get("goTo");
				if (goTo === "address") {
					this.openAddressLookup();
				}
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			// LSP-16289: persist the in-progress Request Content fields before the same-tab navigation
			// to the instruction editor. Only the minimal serializable shape is kept.
			saveInstructionCreateDraft() {
				this.instructionReturn.persistDraft({
					phone: this.phone,
					name: this.name,
					email: this.email,
					includeEmailInvitation: this.includeEmailInvitation,
					notificationTemplate: this.notificationTemplate,
					selectedProjectId: this.newSelectedProject?.id ?? null,
					// Persist the contact id so the project↔contact association at submit (start() reads
					// this.selectedCustomer?.id) survives the editor detour, matching Take Photos.
					selectedCustomerId: this.selectedCustomer?.id ?? null,
					scribePackSelection: this.scribePackSelection ? normalizeScribePackSelectionDraft(this.scribePackSelection) : null,
					scribeUserInputPending: this.scribeUserInputPending,
					instructionId: this.instructionId
				});
			},
			// Restore the draft saved by saveInstructionCreateDraft(). A returned `initialInstructionId`
			// takes precedence over the draft's (now stale) instructionId.
			async restoreInstructionCreateDraft() {
				const draft = this.instructionReturn.loadRawDraft();
				// LSP-20840: nothing to restore (no draft, no returned id) → the default may preselect.
				this.instructionPreselectAllowed = !draft && this.initialInstructionId === null;
				if (draft) {
					if (typeof draft.phone === "string") {
						this.phone = draft.phone;
					}
					if (typeof draft.name === "string") {
						this.name = draft.name;
					}
					if (typeof draft.email === "string") {
						this.email = draft.email;
					}
					if (typeof draft.includeEmailInvitation === "boolean") {
						this.includeEmailInvitation = draft.includeEmailInvitation;
					}
					if (typeof draft.notificationTemplate === "string") {
						this.notificationTemplate = draft.notificationTemplate;
					}
					if (
						!this.scribePackSelectionTouched &&
						Object.prototype.hasOwnProperty.call(draft, "scribePackSelection") &&
						(draft.scribePackSelection === null || isValidScribePackSelectionDraft(draft.scribePackSelection))
					) {
						this.scribePackSelection =
							draft.scribePackSelection === null ? null : normalizeScribePackSelectionDraft(draft.scribePackSelection);
						if (typeof draft.scribeUserInputPending === "boolean") {
							this.scribeUserInputPending = draft.scribeUserInputPending;
						}
					}
					if (typeof draft.selectedProjectId === "string" && draft.selectedProjectId) {
						try {
							this.newSelectedProject = await api.getProject(draft.selectedProjectId);
						} catch (error) {
							console.warn("Unable to restore the selected project from the Request Content draft", error);
						}
					}
					if (typeof draft.selectedCustomerId === "string" && draft.selectedCustomerId) {
						try {
							const result = await api.getCustomers({ where: { id: draft.selectedCustomerId } });
							if (result.count > 0) {
								// Set the object directly (not via setCustomer) so the separately-restored
								// name/email/phone text the user may have edited is not clobbered by the
								// customer's stored values; the id is what start() needs for the association.
								this.selectedCustomer = getCustomerSearchLabel(result.rows[0]);
							} else {
								console.warn("Request Content draft: selected contact no longer resolves on restore", {
									selectedCustomerId: draft.selectedCustomerId
								});
							}
						} catch (error) {
							console.warn("Unable to restore the selected contact from the Request Content draft", error);
						}
					} else if (draft.selectedCustomerId === null) {
						// The user explicitly cleared the contact before the detour. Drop only the customer
						// object (not via setCustomer, which would clobber the separately-restored
						// name/email/phone) so a mounted() project prefill cannot reintroduce a removed contact.
						this.selectedCustomer = null;
					}
				}
				this.instructionId = this.instructionReturn.resolveInstructionId(
					draft?.instructionId,
					this.instructionReturn.returnedInstructionId(this.initialInstructionId),
					this.instructionId
				);
				this.instructionReturn.clearDraft();
			}
		}
	};
</script>

<style lang="scss" scoped>
	.start-conversation-button {
		font-family: "Inter Tight", sans-serif;
		font-size: 20px;
		color: white;
		margin-left: 10px;
	}
	.conversation-card {
		border-radius: 12px;
		min-height: 400px;
		display: flex;
		flex-direction: column;
		justify-content: start;
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 12px;
		margin-top: 12px;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card .header-description {
		color: #b3b3b3;
		font-size: 14px;
		margin-bottom: 8px;
	}

	h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .header {
		color: #000;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.v-slide-group__next,
	.v-slide-group__prev {
		display: none;
	}

	/* Email Invite */

	.team-member-share {
		position: absolute;
		padding-top: 8px;
		margin-left: 2px;
		color: rgb(var(--v-theme-brand));
	}
</style>
